import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router';
import { motion } from 'framer-motion';
import { useToast } from '../../contexts/ToastContext';
import {
  Award,
  Search,
  RefreshCw,
  Upload,
  FileCheck,
  X,
  ChevronDown,
  ChevronUp,
  Loader2,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import {
  getAdminCertificates,
  getAdminCourses,
  getAdminWorkshops,
  generateCertificates,
  revokeCertificate,
  uploadSignature,
} from '../../api/client';

interface Cert {
  id: number;
  certificate_number: string;
  student_name: string;
  student_email?: string;
  course_name: string;
  course_title?: string;
  workshop_title?: string;
  completion_date: string;
  issue_date: string;
  signature_url?: string;
  status: string;
  created_at: string;
}

export default function AdminCertificates(): React.JSX.Element {
  const toast = useToast();
  const navigate = useNavigate();
  const [certs, setCerts] = useState<Cert[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'revoked'>('all');

  // Generate modal
  const [showGenModal, setShowGenModal] = useState(false);
  const [genType, setGenType] = useState<'course' | 'workshop'>('course');
  const [courses, setCourses] = useState<any[]>([]);
  const [workshops, setWorkshops] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [signatureUrl, setSignatureUrl] = useState('');
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadCerts();
  }, [filter]);

  useEffect(() => {
    if (showGenModal) {
      loadOptions();
    }
  }, [showGenModal, genType]);

  const loadCerts = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (filter !== 'all') params.status = filter;
      const res = await getAdminCertificates(params);
      setCerts(res.certificates || []);
    } catch (e: any) {
      toast.show(e.message || 'Failed to load certificates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadOptions = async () => {
    try {
      const [cRes, wRes] = await Promise.all([
        getAdminCourses({ status: 'published' }).catch(() => ({ data: [] })),
        getAdminWorkshops().catch(() => ({ data: { workshops: [] } })),
      ]);
      setCourses(Array.isArray(cRes.data) ? cRes.data : cRes.data?.data || []);
      setWorkshops(wRes.data?.workshops || []);
    } catch {}
  };

  const handleGenerate = async () => {
    if (!selectedId) {
      toast.show('Please select a course or workshop', 'error');
      return;
    }
    setGenerating(true);
    try {
      const payload: any = { signature_url: signatureUrl || undefined };
      if (genType === 'course') payload.course_id = parseInt(selectedId);
      else payload.workshop_id = parseInt(selectedId);
      const res = await generateCertificates(payload);
      toast.show(`${res.count || 0} certificate(s) generated`, 'success');
      setShowGenModal(false);
      setSelectedId('');
      loadCerts();
    } catch (e: any) {
      toast.show(e.message || 'Failed to generate', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async (id: number) => {
    if (!confirm('Revoke this certificate? The student will no longer be able to view it.')) return;
    try {
      await revokeCertificate(id);
      toast.show('Certificate revoked', 'success');
      loadCerts();
    } catch (e: any) {
      toast.show(e.message || 'Failed to revoke', 'error');
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadSignature(file);
      setSignatureUrl(res.url || '');
      toast.show('Signature uploaded', 'success');
    } catch (err: any) {
      toast.show(err.message || 'Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  const filtered = certs.filter(c =>
    (c.student_name?.toLowerCase() || '').includes(search.toLowerCase()) ||
    (c.course_name?.toLowerCase() || '').includes(search.toLowerCase()) ||
    (c.certificate_number?.toLowerCase() || '').includes(search.toLowerCase())
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Award className="text-amber-500" size={24} />
            Certificates
          </h1>
          <p className="text-sm text-slate-500 mt-1">Generate and manage student certificates</p>
        </div>
        <button
          onClick={() => setShowGenModal(true)}
          className="px-4 py-2 bg-slate-900 text-white rounded-xl text-sm font-bold hover:bg-slate-800 transition-colors flex items-center gap-2"
        >
          <FileCheck size={16} />
          Generate Certificates
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            type="text"
            placeholder="Search by student, course, or cert ID..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
          />
        </div>
        <div className="flex gap-2">
          {(['all', 'active', 'revoked'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold capitalize transition-colors ${
                filter === f ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <button onClick={loadCerts} className="p-2.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors">
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin text-slate-400" size={24} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 bg-slate-50 rounded-2xl border border-slate-100">
          <Award size={40} className="mx-auto text-slate-300 mb-3" />
          <h3 className="text-lg font-bold text-slate-900 mb-1">No certificates yet</h3>
          <p className="text-sm text-slate-500 mb-4">Generate certificates for completed enrollments</p>
          <button
            onClick={() => setShowGenModal(true)}
            className="px-4 py-2 bg-amber-500 text-white rounded-xl text-sm font-bold hover:bg-amber-600 transition-colors"
          >
            Generate Now
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Certificate #</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Student</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Course / Workshop</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Completion</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Issued</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Status</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((cert) => (
                  <tr key={cert.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{cert.certificate_number}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{cert.student_name}</div>
                      {cert.student_email && <div className="text-xs text-slate-400">{cert.student_email}</div>}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{cert.course_name}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{cert.completion_date ? new Date(cert.completion_date).toLocaleDateString() : '-'}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{cert.issue_date ? new Date(cert.issue_date).toLocaleDateString() : '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                        cert.status === 'active'
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-red-50 text-red-700'
                      }`}>
                        {cert.status === 'active' ? <CheckCircle size={10} /> : <AlertCircle size={10} />}
                        {cert.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/certificate/${cert.id}`}
                          target="_blank"
                          className="px-3 py-1.5 text-xs font-semibold text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                        >
                          View
                        </Link>
                        {cert.status === 'active' && (
                          <button
                            onClick={() => handleRevoke(cert.id)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Revoke"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Generate Modal */}
      {showGenModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden"
          >
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-900">Generate Certificates</h3>
              <button onClick={() => setShowGenModal(false)} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-lg">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Type toggle */}
              <div className="flex gap-2">
                <button
                  onClick={() => { setGenType('course'); setSelectedId(''); }}
                  className={`flex-1 px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${
                    genType === 'course' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  Course
                </button>
                <button
                  onClick={() => { setGenType('workshop'); setSelectedId(''); }}
                  className={`flex-1 px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${
                    genType === 'workshop' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  Workshop
                </button>
              </div>

              {/* Select */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Select {genType === 'course' ? 'Course' : 'Workshop'}
                </label>
                <select
                  value={selectedId}
                  onChange={e => setSelectedId(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                >
                  <option value="">Choose...</option>
                  {(genType === 'course' ? courses : workshops).map((item: any) => (
                    <option key={item.id} value={item.id}>{item.title}</option>
                  ))}
                </select>
              </div>

              {/* Signature upload */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Admin Signature (optional)</label>
                <div className="flex items-center gap-3">
                  {signatureUrl ? (
                    <img src={signatureUrl} alt="Signature" className="h-12 object-contain border rounded-lg px-2 bg-white" />
                  ) : (
                    <div className="h-12 flex-1 flex items-center px-3 rounded-xl border border-dashed border-slate-300 text-xs text-slate-400">
                      No signature uploaded
                    </div>
                  )}
                  <label className="cursor-pointer px-3 py-2 bg-slate-100 text-slate-700 rounded-xl text-xs font-semibold hover:bg-slate-200 transition-colors flex items-center gap-1.5 shrink-0">
                    <Upload size={14} />
                    {uploading ? 'Uploading...' : 'Upload'}
                    <input type="file" accept="image/*" className="hidden" onChange={handleFileUpload} disabled={uploading} />
                  </label>
                </div>
                <p className="text-xs text-slate-400 mt-1">PNG/JPG, max 2MB. Will appear on all generated certificates.</p>
              </div>

              <div className="bg-amber-50 rounded-xl p-3 text-xs text-amber-800">
                <p className="font-semibold mb-1">What happens?</p>
                <p>Certificates will be created for all students with <strong>completed</strong> status. Already-issued certificates are skipped.</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-100">
              <button
                onClick={() => setShowGenModal(false)}
                className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={!selectedId || generating}
                className="px-4 py-2 bg-slate-900 text-white rounded-xl text-sm font-bold hover:bg-slate-800 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {generating && <Loader2 size={14} className="animate-spin" />}
                {generating ? 'Generating...' : 'Generate'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
